/**
 * One Production Line (production-lines): the coordinator that reads the
 * page model from `useProductionLineDetail` and composes the head, the five
 * tabs and the modals (brief, edit profile, pause, archive). Every section
 * lives in its own sibling file; this file only wires them together.
 */

import type { ReactNode } from 'react';

import { EditProfileModal } from '../business-profile/BusinessProfilePage.js';
import {
  Button,
  ButtonLink,
  Callout,
  EmptyState,
  Modal,
  Tabs,
  WarningIcon,
} from '../../merchant/ui/primitives.js';
import { NewProductionLineModal } from './NewProductionLineModal.js';
import { ProductionLineDetailHeader } from './ProductionLineDetailHeader.js';
import { ProductionLineOverviewTab } from './ProductionLineOverviewTab.js';
import { ProductionLineBriefTab } from './ProductionLineBriefTab.js';
import { ProductionLineAnalysisTab } from './ProductionLineAnalysisTab.js';
import { ProductionLineVideosTab } from './ProductionLineVideosTab.js';
import { ProductionLineActivityTab } from './ProductionLineActivityTab.js';
import { TABS } from './productionLineJourney.js';
import { useProductionLineDetail } from './useProductionLineDetail.js';

/**
 * One Production Line, from the wireframe: the head with its status and
 * actions, five tabs, and — in Overview — the four-step journey the server
 * reports: Business Profile → Analysis → Recommendations → Creating your
 * video, then the completed video or the honest failure.
 *
 * Every step is read from the intake behind the line. Nothing advances on a
 * timer; the page only asks the server again while it says work is live.
 */
export function ProductionLineDetailPage(): ReactNode {
  const {
    language,
    copy,
    navigate,
    toast,
    workspace,
    productionLineId,
    tab,
    titleId,
    online,
    detailFailed,
    moreOpen,
    setMoreOpen,
    modal,
    setModal,
    busy,
    actionError,
    selectedDirection,
    setSelectedDirection,
    askingAngle,
    setAskingAngle,
    setAnotherVersion,
    summary,
    realLine,
    journeyState,
    view,
    workspaceName,
    lineVideos,
    latestRun,
    journey,
    status,
    domain,
    prompt,
    title,
    updatedAt,
    startVideo,
    makeIt,
    askAngle,
    makeAnotherOnLine,
    lifecycle,
  } = useProductionLineDetail();

  const tabs = TABS.map((key) => ({
    key,
    label: copy.line.tabs[key],
    href: `/production-lines/${encodeURIComponent(productionLineId)}${key === 'overview' ? '' : `/${key}`}`,
    count:
      key === 'videos'
        ? lineVideos.filter((v) => v.status === 'ready' || v.status === 'needs-review')
            .length
        : undefined,
  }));

  if (detailFailed && summary === null && view === null) {
    return (
      <section className="mj-ws-page" aria-labelledby={titleId}>
        <EmptyState
          body={copy.lines.lineMissing}
          action={
            <ButtonLink to="/production-lines" small>
              {copy.common.backToLines}
            </ButtonLink>
          }
        />
      </section>
    );
  }

  return (
    <section
      className="mj-ws-page"
      aria-labelledby={titleId}
      data-screen-label="Production Line detail"
    >
      <ProductionLineDetailHeader
        copy={copy}
        language={language}
        titleId={titleId}
        productionLineId={productionLineId}
        title={title}
        status={status}
        domain={domain}
        prompt={prompt}
        updatedAt={updatedAt}
        summary={summary}
        realLine={realLine}
        busy={busy}
        moreOpen={moreOpen}
        setMoreOpen={setMoreOpen}
        setModal={setModal}
        lifecycle={lifecycle}
        navigate={navigate}
        toast={toast}
      />

      <Tabs label={copy.line.sections} active={tab} tabs={tabs} />

      {actionError !== null ? (
        <Callout className="mj-ws-mt-8" role="alert">
          <WarningIcon />
          <span className="mj-ws-callout-copy mj-ws-sm">{actionError}</span>
        </Callout>
      ) : null}
      {journeyState.error !== null && tab === 'overview' ? (
        <Callout className="mj-ws-mt-8" role="alert">
          <WarningIcon />
          <span className="mj-ws-callout-copy mj-ws-sm">{journeyState.error}</span>
          <Button small onClick={journeyState.clearError}>
            {copy.common.close}
          </Button>
        </Callout>
      ) : null}

      {tab === 'overview' ? (
        <ProductionLineOverviewTab
          copy={copy}
          language={language}
          productionLineId={productionLineId}
          journey={journey}
          view={view}
          journeyState={journeyState}
          workspaceName={workspaceName}
          domain={domain}
          prompt={prompt}
          title={title}
          summary={summary}
          realLine={realLine}
          latestRun={latestRun}
          online={online}
          busy={busy}
          selectedDirection={selectedDirection}
          setSelectedDirection={setSelectedDirection}
          askingAngle={askingAngle}
          setAskingAngle={setAskingAngle}
          setAnotherVersion={setAnotherVersion}
          setModal={setModal}
          startVideo={startVideo}
          makeIt={makeIt}
          askAngle={askAngle}
          makeAnotherOnLine={makeAnotherOnLine}
          toast={toast}
        />
      ) : null}

      {tab === 'brief' ? (
        <ProductionLineBriefTab
          copy={copy}
          language={language}
          summary={summary}
          prompt={prompt}
          title={title}
          workspaceName={workspaceName}
          view={view}
          setModal={setModal}
        />
      ) : null}

      {tab === 'analysis' ? (
        <ProductionLineAnalysisTab
          copy={copy}
          language={language}
          productionLineId={productionLineId}
          view={view}
          journey={journey}
          journeyState={journeyState}
        />
      ) : null}

      {tab === 'videos' ? (
        <ProductionLineVideosTab
          copy={copy}
          language={language}
          productionLineId={productionLineId}
          lineVideos={lineVideos}
        />
      ) : null}

      {tab === 'activity' ? (
        <ProductionLineActivityTab
          copy={copy}
          language={language}
          summary={summary}
          view={view}
          lineVideos={lineVideos}
          realLine={realLine}
          domain={domain}
        />
      ) : null}

      {modal === 'brief' ? (
        <NewProductionLineModal
          editing
          initialUrl={summary?.websiteUrl ?? ''}
          initialPrompt={prompt}
          onClose={() => {
            setModal(null);
          }}
          onCreated={(newIntakeId, lineId) => {
            setModal(null);
            void navigate(
              lineId === null
                ? `/business-profile/business?intake=${encodeURIComponent(newIntakeId)}`
                : `/production-lines/${encodeURIComponent(lineId)}`,
            );
          }}
        />
      ) : null}
      {modal === 'edit-profile' && view?.profile ? (
        <EditProfileModal
          copy={copy}
          language={language}
          workspaceName={workspaceName}
          payload={view.profile.payload}
          busy={journeyState.working}
          onClose={() => {
            setModal(null);
          }}
          onSave={(next) => {
            void journeyState
              .correct(next, 'Merchant corrected the generated business profile.')
              .then((ok) => {
                if (ok) {
                  setModal(null);
                  toast(copy.profile.saved);
                  void workspace.refresh();
                }
              });
          }}
        />
      ) : null}
      {modal === 'pause' || modal === 'archive' ? (
        <Modal
          title={modal === 'pause' ? copy.line.pauseConfirm : copy.line.archiveConfirm}
          description={modal === 'pause' ? copy.line.pauseBody : copy.line.archiveBody}
          onClose={() => {
            setModal(null);
          }}
          onSubmit={() => {
            lifecycle(modal);
          }}
          footer={
            <>
              <Button
                onClick={() => {
                  setModal(null);
                }}
              >
                {copy.common.cancel}
              </Button>
              <Button variant="primary" type="submit" disabled={busy !== null}>
                {modal === 'pause' ? copy.line.pause : copy.line.archive}
              </Button>
            </>
          }
        >
          <span className="mj-ws-vh">{title}</span>
        </Modal>
      ) : null}
    </section>
  );
}
